import { generateRandomId } from '@sparklayer-io/spark-dash-ui/lib/random';
import { Alert, Button, Card, Input } from '@ui/themed';
import { t } from 'i18next';
import { stringToSlug } from '../../../utils/slug';
import { useShippingMethodForm } from '../builder-context';
import { MAX_NAME_LENGTH } from '../schemas';
import { BandCost } from './band-cost';
import { BandRequirement } from './band-requirement';

export function ShippingBandBuilder({ isEditing }: { isEditing?: boolean }) {
  const form = useShippingMethodForm();

  // Shows all bands, as well as a button to add another
  return (
    <form.Field name="bands" mode="array">
      {(field) => (
        <>
          {field.state.value.map((band, i) => (
            <Card key={band.key}>
              <Card.Header headingLevel="h2" collapsible>
                {t(
                  `features:shipping_methods.components.shipping_band_builder.${band.name.length > 0 ? 'title_with_name' : 'title'}`,
                  { i: i + 1, name: band.name },
                )}
              </Card.Header>
              <Card.Body>
                {/* Shipping band name */}
                <form.Field
                  name={`bands[${i}].name`}
                  listeners={{
                    // NOTE: When using a 'custom' band cost, an additional 'costLanguageString'
                    // field is created. It's value is derived from the this band's name field, and
                    // cannot be be changed. Therefore, we update the cost language string whenever
                    // the band name changes.
                    onChange: ({ value, fieldApi }) => {
                      // Don't allow changing when editing - could be set through the API, and we
                      // don't want to overwrite
                      if (isEditing) {
                        return;
                      }

                      const costPath = `bands[${i}].cost` as const;
                      const currentCost = fieldApi.form.getFieldValue(costPath);

                      // Cost language string is only for "custom" band cost
                      if (currentCost?.type === 'custom') {
                        // Language string is derived entirely from the band name
                        fieldApi.form.setFieldValue(costPath, {
                          type: 'custom',
                          costLanguageString: stringToSlug(value),
                        });
                      }
                    },
                  }}
                >
                  {(field) => (
                    <Input
                      label={t(
                        'features:shipping_methods.components.shipping_band_builder.name.label',
                      )}
                      placeholder={t(
                        'features:shipping_methods.components.shipping_band_builder.name.placeholder',
                      )}
                      description={t(
                        'features:shipping_methods.components.shipping_band_builder.name.description',
                      )}
                      maxLength={MAX_NAME_LENGTH}
                      value={field.state.value}
                      onChange={field.handleChange}
                      error={field.state.meta.errors[0]?.message}
                    />
                  )}
                </form.Field>

                <BandCost band={band} index={i} />
                <BandRequirement band={band} index={i} />

                <Button variant="dangerOutline" icon="delete" onClick={() => field.removeValue(i)}>
                  {t('buttons:delete')}
                </Button>
              </Card.Body>
            </Card>
          ))}

          {/* Show alert if no bands are created */}
          <form.Subscribe selector={(state) => state.fieldMeta.bands?.errors}>
            {(errors) => {
              const bandLengthError = errors?.some(
                (error) => error.origin === 'array' && error.code === 'too_small',
              );
              if (!bandLengthError) return null;

              return (
                <Alert variant="error" headingLevel="h2">
                  {t('features:shipping_methods.components.shipping_band_builder.no_bands')}
                </Alert>
              );
            }}
          </form.Subscribe>

          {/* Add a new band */}
          <Button
            icon="plusCircle"
            variant="secondary"
            onClick={() =>
              field.pushValue({
                key: generateRandomId(),
                name: '',
                sku: undefined,
                priority: '0',
                cost: { type: 'free' },
                requirement: { type: 'none' },
              })
            }
          >
            {t('features:shipping_methods.components.shipping_band_builder.actions.new')}
          </Button>
        </>
      )}
    </form.Field>
  );
}
